import { useCallback, useRef } from 'react';

type Callback<P extends unknown[], R> = (...args: P) => R;

/**
 * A hook that uses a {@link MutableRefObject} to store a function reference such that the function
 * can access the most up to date values in state and/or in the render cycle without having to
 * change it's reference each time those dependencies change.
 *
 * When the function is stored as a reference, it does not trigger rerenders or dependency changes
 * to the effect - and instead of re-creating the function when its dependencies on state and/or
 * props change, the same function reference is used, but just called with the updated dependencies.
 *
 * This is a documented React "escape-hatch", that can be used as a performance optimization ONLY
 * in cases where the callback function is an event handler, such as an 'onClick' or 'onClose'
 * handler, and it does not get called during render.
 *
 * If the callback is an event handler that does not get called during rendering, this hook can be
 * applicable for the following cases:
 *
 * 1. The callback is a dependency to an effect, but the effect's behavior alters state or props
 *    that are also dependencies to the callback.  Normally, this would lead to an infinite
 *    recursion, as the effect will change the state and/or props, the callback's reference will
 *    change, and then the effect (which depends on the callback) will fire again.
 *
 * 2. It is important that the callback does not change it's reference more than once.  This may be
 *    the case if the callback is coming from a general hook and is being propogated down through
 *    many layers of components.
 *
 * @example
 * // Example of Situation 1
 * const MyComponent = () => {
 *   const [data, setData] = useState([]);
 *   const [error, setError] = useState(false);
 *
 *   const fn = useReferentialCallback(() => {
 *     const response = await fetch(`/api/data/${initialId}`);
 *     if (response.ok) {
 *       setData(response.data);
 *     } else {
 *       setError(true)
 *     }
 *   })
 *
 *   useEffect(() => {
 *     if (data.length === 0 && !error) {
 *       fn()
 *     }
 *   }, [data, fn, error])
 * }
 *
 * @deprecated Deprecated in favor of React 19's useEffectEvent.
 */
export const useReferentialCallback = <P extends unknown[], R>(fn: Callback<P, R>) => {
  const ref = useRef<Callback<P, R>>(() => {
    throw new Error('The referential callback cannot be called during rendering!');
  });
  ref.current = fn;
  /* In a traditional useCallback, when the args change the function will be recreated with a
     different reference. Here, the same reference is used, but the reference is called with the
     updated arguments. */
  const func: Callback<P, R> = (...args: P) => ref.current.apply(ref.current, args);
  return useCallback(func, []);
};
